/**
 * Hook qui récupère en bulk les locks actifs pour une liste d'IDs et
 * les rafraîchit périodiquement.
 *
 * Retourne un dictionnaire `{ [id]: { user_id, user_name, expires_at } }`
 * contenant UNIQUEMENT les ressources actuellement lockées par quelqu'un.
 *
 * Usage :
 *   const locks = useBulkLocks('devis', devisList.map(d => d.id));
 *   const isLocked = !!locks[d.id];
 *   const lockedBy = locks[d.id]?.user_name;
 */
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function useBulkLocks(resourceType, ids, { pollMs = 5000, enabled = true } = {}) {
    const [locks, setLocks] = useState({});
    // Clé stable de la liste pour éviter les re-fetchs inutiles quand l'ordre change
    const idsKey = (ids || []).filter(Boolean).sort().join(',');
    const idsRef = useRef([]);
    idsRef.current = (ids || []).filter(Boolean);

    useEffect(() => {
        if (!enabled || !resourceType || !idsRef.current.length) {
            setLocks({});
            return undefined;
        }
        let stopped = false;

        const fetchLocks = async () => {
            try {
                const { data } = await axios.post(
                    `${API}/api/locks/bulk`,
                    { resource_type: resourceType, ids: idsRef.current },
                    { withCredentials: true }
                );
                if (stopped) return;
                const next = data || {};
                // Évite les re-renders inutiles : ne setState que si les locks ont réellement changé.
                setLocks(prev => {
                    const pk = Object.keys(prev);
                    const nk = Object.keys(next);
                    if (pk.length !== nk.length) return next;
                    for (const k of nk) {
                        if (!prev[k] || prev[k].user_id !== next[k].user_id || prev[k].expires_at !== next[k].expires_at) {
                            return next;
                        }
                    }
                    return prev;
                });
            } catch { /* silencieux — on ne veut pas polluer l'UI */ }
        };

        fetchLocks();
        const timer = setInterval(fetchLocks, pollMs);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') fetchLocks();
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            stopped = true;
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourceType, idsKey, enabled, pollMs]);

    return locks;
}
